import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  EMAIL_VERIFICATION_REQUIRED: 'EMAIL_VERIFICATION_REQUIRED',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_USER: 'SET_USER',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.EMAIL_VERIFICATION_REQUIRED:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        emailVerificationRequired: true,
        emailVerificationData: action.payload,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user on app start - DISABLED for landing page only mode
  useEffect(() => {
    // Skip loading user since we're in landing page only mode
    dispatch({
      type: AUTH_ACTIONS.SET_LOADING,
      payload: false,
    });
  }, []); // Empty dependency array - only run once on mount

  // Login function - DISABLED for landing page only mode
  const login = useCallback(async (credentials) => {
    console.warn('Login is disabled - Landing page only mode');
    dispatch({
      type: AUTH_ACTIONS.LOGIN_FAILURE,
      payload: 'Authentication is disabled - Landing page only mode',
    });
    throw new Error('Authentication is disabled - Landing page only mode');
  }, []);

  // Register function - DISABLED for landing page only mode
  const register = useCallback(async (userData) => {
    console.warn('Registration is disabled - Landing page only mode');
    dispatch({
      type: AUTH_ACTIONS.REGISTER_FAILURE,
      payload: 'Registration is disabled - Landing page only mode',
    });
    throw new Error('Registration is disabled - Landing page only mode');
  }, []);

  // Logout function - DISABLED for landing page only mode
  const logout = useCallback(async () => {
    console.warn('Logout is disabled - Landing page only mode');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  }, []);

  // Email verification function - DISABLED for landing page only mode
  const verifyEmail = useCallback(async (email, verificationCode) => {
    console.warn('Email verification is disabled - Landing page only mode');
    dispatch({
      type: AUTH_ACTIONS.LOGIN_FAILURE,
      payload: 'Email verification is disabled - Landing page only mode',
    });
    throw new Error('Email verification is disabled - Landing page only mode');
  }, []);

  // Resend verification function - DISABLED for landing page only mode
  const resendVerification = useCallback(async (email) => {
    console.warn('Resend verification is disabled - Landing page only mode');
    dispatch({
      type: AUTH_ACTIONS.LOGIN_FAILURE,
      payload: 'Resend verification is disabled - Landing page only mode',
    });
    throw new Error('Resend verification is disabled - Landing page only mode');
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  const value = {
    ...state,
    login,
    register,
    logout,
    verifyEmail,
    resendVerification,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
